import { useState } from 'react'
import clsx from 'clsx'
import { CSSTransition } from 'react-transition-group'
import { Plus, ChevronRight, Folder, FolderPlus, Home } from 'react-feather'
import { Icon } from '@iconify/react'
import { Link } from 'react-router-dom'

import {
  Avatar,
  useAuthState,
  useGlobalState,
  DocBriefDetailsModal,
  useGetBriefs,
} from '@pepper/ui'

import { useSelectedCompanyId } from 'utils/functions/misc'
import { OrgModalType } from 'container/Dashboard/Dashboard'
import CreateMenu from 'components/CreateMenu'
import CreateProject from '../CreateProject'
import pepperLogo from 'assets/images/pepper_logo_dark.png'

import MenuItem from './MenuItem'
import UserProfile from './UserProfile'
import BulkDownloadStatus from './BulkDownloadStatus/BulkDownloadStatus'
import s from './Navbar.module.css'

const ICON_SIZE = {
  small: 16,
  regular: 18,
}

type NavbarProps = {
  collapseMenu: boolean
  setJoinOrganisationModal?: (args: OrgModalType) => void
  onClose?: () => void
}

// TODO: @jibin2706 move this to company settings api
const ASSET_MANAGER_ORG = [
  '1M1aqv3v9yn', // Stephine B (dev)
  '1Ekm3E6AwRp', // Dane Watson (prod)
  '1bD4zVEaa4d', // Infineon CMP (prod)
  '1Ekm32ajx4R', // Infineon (prod)
]

const Navbar = ({
  collapseMenu,
  setJoinOrganisationModal,
  onClose,
}: NavbarProps): JSX.Element => {
  const { user } = useAuthState()
  const companyId = useSelectedCompanyId()
  const { viewport } = useGlobalState()
  const [showDocBriefModal, setDocBriefModal] = useState(false)
  const [showUserProfile, setShowUserProfile] = useState(false)
  const [showProjectModal, setShowProjectModal] = useState(false)

  const isMobileView = viewport !== 'lg'
  const selectedCompany = user.company_details.find(e => e.id === companyId)
  const isShrunk = !isMobileView && collapseMenu
  const iconSize = isShrunk ? ICON_SIZE.regular : ICON_SIZE.small

  const briefs = useGetBriefs({ data: { companyId, include_drafts: true } })

  return (
    <>
      <CSSTransition
        in={!showUserProfile}
        timeout={300}
        classNames={{
          enter: s.navIn,
          enterActive: s.navInActive,
          exit: s.navOut,
          exitActive: s.navOutActive,
        }}
        unmountOnExit={true}
      >
        <nav
          className={clsx(s.navbar, {
            [s.isShrunk]: isShrunk,
          })}
        >
          <div className={s.brand}>
            <Link to="/home">
              <img src={pepperLogo} width="164" alt="Pepper Content logo" />
            </Link>
          </div>
          <menu className={s.menu}>
            <MenuItem
              label="Home"
              path="/home"
              icon={<Home size={iconSize} />}
              onClick={onClose}
              isShrunk={isShrunk}
              dataId="business-navbar-home-navigation-link"
            />

            <MenuItem
              label="AI Content Studio"
              path="/content-studio"
              icon={<Icon icon="lucide:sparkles" height={iconSize} />}
              onClick={onClose}
              isShrunk={isShrunk}
              dataId="business-navbar-content-studio-link"
              new={true}
            />

            <MenuItem
              label="Content Ideas"
              path="/content-ideas"
              icon={<Icon icon="lucide:brain" height={iconSize} />}
              onClick={onClose}
              isShrunk={isShrunk}
              dataId="business-navbar-content-ideas-link"
            />

            <span>
              <MenuItem
                label="Projects & Items"
                path="/projects"
                icon={<Folder size={iconSize} />}
                onClick={onClose}
                isShrunk={isShrunk}
                dataId="projects-items-navbar"
              />
            </span>

            <MenuItem
              label="Website Content Audit"
              path="/content-refresh"
              icon={<Icon icon="lucide:globe" height={iconSize} />}
              onClick={onClose}
              isShrunk={isShrunk}
              dataId="business-navbar-content-refresh-link"
            />

            <MenuItem
              label="Analytics & ROI"
              path="/roi"
              icon={<Icon icon="uil:graph-bar" height={iconSize} />}
              onClick={onClose}
              isShrunk={isShrunk}
              dataId="business-navbar-roi-link"
            />

            {ASSET_MANAGER_ORG.includes(companyId) && (
              <MenuItem
                label="Asset Manager"
                path="/asset-manager"
                icon={<Icon icon="lucide:cassette-tape" height={iconSize} />}
                onClick={onClose}
                isShrunk={isShrunk}
                dataId="business-navbar-asset-manager-link"
              />
            )}
          </menu>

          {showDocBriefModal && (
            <DocBriefDetailsModal
              platform="business"
              showDocBriefModal={showDocBriefModal}
              setShowBriefModal={setDocBriefModal}
              briefs={briefs?.data}
              companyId={companyId}
            />
          )}

          <div className={s.floatingButton}>
            {!isShrunk && <BulkDownloadStatus />}
            <CreateMenu>
              <button
                className={clsx(s.primaryButton)}
                type="button"
                data-tooltip="Create"
              >
                {isShrunk ? (
                  <FolderPlus size={16} />
                ) : (
                  <>
                    <Plus size={16} /> Create
                  </>
                )}
              </button>
            </CreateMenu>
          </div>

          {isMobileView && (
            <button
              className={s.profileTrigger}
              type="button"
              data-id="business-topbar-avatar-button"
              onClick={() => setShowUserProfile(true)}
            >
              <Avatar
                className={s.topBarAvatar}
                size="default"
                src={user.avatar ?? ''}
              />
              <div className={s.userInfo}>
                <h4 className={s.userName}>{user.first_name}</h4>
                <p className={s.userOrg}>{selectedCompany?.company_name}</p>
              </div>
              <ChevronRight size={iconSize} />
            </button>
          )}
        </nav>
      </CSSTransition>

      <CSSTransition
        in={showUserProfile}
        timeout={300}
        classNames={{
          enter: s.profileIn,
          enterActive: s.profileInActive,
          exit: s.profileOut,
          exitActive: s.profileOutActive,
        }}
        unmountOnExit={true}
      >
        <UserProfile
          onBack={() => setShowUserProfile(false)}
          setJoinOrganisationModal={setJoinOrganisationModal}
          onClose={onClose}
        />
      </CSSTransition>
      <CreateProject
        companyId={companyId}
        isOpen={showProjectModal}
        onClose={() => setShowProjectModal(false)}
      />
    </>
  )
}

export default Navbar
